import { useEffect, useState } from 'react';

import { Col, Row } from '@dec/shared';
import { Button } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { Combobox, HookForm, Text } from '@dec/shared';
import { BtnGroup } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { descriptions as descCommon, label as lblCommon } from 'app/modules/translation';
import { useTranslation } from 'react-i18next';

import { useCatalogValueList } from '@/app/modules/configuration/catalogs/hooks/useCatalogValueList';
import {
  type CatalogModel,
  catalogName
} from '@/app/modules/configuration/catalogs/models/Catalog';

import { useTaxpayerRequestStore } from '../../stores/useTaxpayerRequestStore';
import { GivenNamesAndSurNames } from '../components/given-names-and-sur-names';
import ProgressBar from '../components/progress-bar';
import { ValidationsResolver } from '../rules';
import { rulesTaxData } from '../rules/tax';
import { label } from '../translation';
import VisibleComponent from '../visible-component';

import { type ProgressStepProps, type TaxStep } from './types';
interface FormProps {
  onSubmit: (data: any) => void;
  taxReturnCurrencies: Array<CatalogModel>;
  defaultValues: TaxStep;
  readonly?: boolean;
}
const enable = true;
export const FormTax = ({ onSubmit, taxReturnCurrencies, defaultValues, readonly }: FormProps) => {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<TaxStep>();

  useEffect(() => {
    setState(defaultValues);
  }, []);

  if (state === undefined) {
    return <></>;
  }

  return (
    <ValidationsResolver rules={rulesTaxData}>
      {({ validations }: any) => {
        return (
          <HookForm
            nameForm={'formTextData'}
            onSubmit={onSubmit}
            validations={validations}
            initialValues={state}
          >
            <VisibleComponent fieldCode="taxIdentificationNumber">
              <Row>
                <Col>
                  <Text readonly={readonly} name="idTax" label={label(t, 'idTax')} />
                </Col>
              </Row>
            </VisibleComponent>
            <GivenNamesAndSurNames enable={enable} propetyName="contactName" readonly={readonly} />
            <Row>
              <Col>
                <Text readonly={readonly} name="contactName" label={label(t, 'contactName-tax')} />
              </Col>

              <VisibleComponent fieldCode="taxContactIdentificationNumber">
                <Col>
                  <Text
                    readonly={readonly}
                    name="idDocNumber"
                    label={label(t, 'docNumberContact-tax')}
                  />
                </Col>
              </VisibleComponent>
            </Row>
            <Row>
              <VisibleComponent fieldCode="taxContactTaxNumber">
                <Col className="mt-auto">
                  <Text
                    readonly={readonly}
                    name="contactTaxIdNumber"
                    label={label(t, 'contactTaxIdNumber-tax')}
                    description={descCommon('contactTaxIdNumber-tax')}
                  />
                </Col>
              </VisibleComponent>
              <VisibleComponent fieldCode="taxPhoneNumber">
                <Col>
                  <div className="d-flex">
                    <div className="w-25 me-2 mt-auto">
                      <Text
                        readonly={readonly}
                        name="phoneNumberArea"
                        label={t('common.label.phoneNumberArea')}
                      />
                    </div>
                    <div>
                      <Text
                        readonly={readonly}
                        name="phoneNumber"
                        label={label(t, 'phoneNumberContact-tax')}
                      />
                    </div>
                  </div>
                </Col>
              </VisibleComponent>
            </Row>
            <Row>
              <Col>
                <Combobox
                  code="id"
                  display="name"
                  data={taxReturnCurrencies}
                  label={lblCommon('taxReturnCurrency')}
                  name="taxReturnCurrency"
                  description={descCommon('taxReturnCurrency')}
                  readOnly={readonly}
                />
              </Col>
              <Col>
                <Text readonly={readonly} name="email" label={label(t, 'email-tax')} />
              </Col>
            </Row>
          </HookForm>
        );
      }}
    </ValidationsResolver>
  );
};

const Tax = (props: ProgressStepProps) => {
  const currentData = useTaxpayerRequestStore((s) => s.form.tax);
  const currentDataGeneral = useTaxpayerRequestStore((s) => s.form.general);
  const addTax = useTaxpayerRequestStore((s) => s.addTax);
  const value = currentDataGeneral?.country?.additionalFields?.phoneNumberArea;
  const phoneNumberArea =
    value && (currentData?.phoneNumberArea === undefined || currentData?.phoneNumberArea === '')
      ? value
      : currentData?.phoneNumberArea;

  const {
    data: taxReturnCurrencies,
    isLoading: loadingTaxReturnCurrency,
    error: errorTaxReturnCurrency
  } = useCatalogValueList(catalogName.taxReturnCurrencies);

  const onSubmit = (data: TaxStep) => {
    addTax(data);
    props.nextStep();
  };
  if (loadingTaxReturnCurrency || errorTaxReturnCurrency) {
    return <></>;
  }
  return (
    <>
      <FormTax
        taxReturnCurrencies={taxReturnCurrencies ?? []}
        defaultValues={
          currentData ? ({ ...currentData, phoneNumberArea } as TaxStep) : (undefined as any)
        }
        onSubmit={onSubmit}
      />
      <ProgressBar percentage={props.percentage} />
      <BtnToolbar>
        <BtnGroup>
          {' '}
          <Button
            form="formTextData"
            action={ButtonAction.previous}
            onClick={() => props.previousStep()}
          />
        </BtnGroup>
        <BtnGroup>
          <Button
            variant={'primary'}
            form="formTextData"
            action={ButtonAction.next}
            type={'submit'}
          />
        </BtnGroup>
      </BtnToolbar>
    </>
  );
};

export default Tax;
